/**
 * PlanResponsesTab: sessions list for an interview plan.
 * Clicking a session opens the SessionDetailView with transcript/entity toggle.
 */

import { useState } from 'react'

import type { AIInterviewSession } from '@ispot/shared'

import { usePlanSessions } from '@/hooks/useAIInterviewPlans'

import { Badge } from '@/components/ui/badge'
import { MessageSquare, User, Loader2, Inbox } from 'lucide-react'

import { SessionDetailView } from './SessionDetailView'

// ============================================================================
// HELPERS
// ============================================================================

function statusVariant(status: string) {
  if (status === 'completed') return 'default' as const
  if (status === 'active') return 'secondary' as const
  return 'outline' as const
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

// ============================================================================
// PROPS
// ============================================================================

interface PlanResponsesTabProps {
  accountId: string
  projectId: string
  planId: string
}

// ============================================================================
// SESSIONS LIST
// ============================================================================

function SessionsList({
  accountId, projectId, planId, onSelect,
}: PlanResponsesTabProps & { onSelect: (id: string) => void }) {
  const { data: sessions = [], isLoading } = usePlanSessions(accountId, projectId, planId)

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-fg-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-accent-500" /> Loading sessions...
      </div>
    )
  }

  if (sessions.length === 0) {
    return (
      <div className="flex h-48 flex-col items-center justify-center text-fg-muted">
        <Inbox className="w-8 h-8 mb-2" />
        <p className="text-sm">No responses yet</p>
        <p className="text-xs mt-1">Share the interview link to start collecting responses</p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-border-subtle">
      {(sessions as AIInterviewSession[]).map((s) => (
        <button
          key={s.id}
          onClick={() => onSelect(s.id)}
          className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-bg-secondary"
        >
          <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-market-50">
            <User className="h-4 w-4 text-market-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="truncate text-body font-medium text-fg">
              {s.intervieweeName || s.linkedPersonName || 'Anonymous'}
            </div>
            <div className="text-caption text-fg-muted">{formatDate(s.createdAt)}</div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="flex items-center gap-1 text-caption text-fg-secondary">
              <MessageSquare className="w-3 h-3" /> {s.messageCount}
            </span>
            <Badge variant={statusVariant(s.status)} className="text-[10px]">
              {s.status}
            </Badge>
          </div>
        </button>
      ))}
    </div>
  )
}

// ============================================================================
// MAIN EXPORT
// ============================================================================

export function PlanResponsesTab({ accountId, projectId, planId }: PlanResponsesTabProps) {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)

  if (selectedSessionId) {
    return (
      <SessionDetailView
        accountId={accountId}
        projectId={projectId}
        sessionId={selectedSessionId}
        onBack={() => setSelectedSessionId(null)}
      />
    )
  }

  return (
    <SessionsList
      accountId={accountId}
      projectId={projectId}
      planId={planId}
      onSelect={setSelectedSessionId}
    />
  )
}
